import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
  useCallback,
} from "react";
import styled from "styled-components";
import { useQuery, useMutation, useLazyQuery, gql } from "@apollo/client";
import AdminUserGrid from "./grids/AdminUserGrid";
import ConfirmDialog from "../../components/ConfirmDialog";
import { isSessionExpiredError } from "../../contexts/SessionExpiredContext";
import { Button as LatitudeButton } from "@flexport/latitude-typescript";
import UserLoginHistoryModal from "./UserLoginHistoryModal";

const ADMIN_USERS = gql`
  query AdminUsers {
    admin {
      users {
        id
        email
        name
        role
        createdAt
        updatedAt
        clientId
        client {
          clientId
          client_name
          folder
        }
        supplierId
        supplier {
          id
          name
          subfolder
        }
      }
      suppliers {
        id
        name
        subfolder
        clientId
      }
    }
  }
`;

const ADMIN_CLIENTS = gql`
  query AdminClients {
    admin {
      clients {
        clientId
        client_name
        folder
      }
    }
  }
`;

const ADMIN_SUPPLIERS = gql`
  query AdminSuppliers {
    admin {
      suppliers {
        id
        name
        subfolder
        clientId
        client {
          clientId
          client_name
        }
      }
    }
  }
`;

const CREATE_USER = gql`
  mutation CreateUser(
    $name: String!
    $email: String
    $role: Role!
    $clientId: ID
    $supplierId: ID
    $supplierName: String
    $supplierSubfolder: String
  ) {
    admin {
      createUser(
        name: $name
        email: $email
        role: $role
        clientId: $clientId
        supplierId: $supplierId
        supplierName: $supplierName
        supplierSubfolder: $supplierSubfolder
      ) {
        id
        email
        name
        role
      }
    }
  }
`;

const UPDATE_USER = gql`
  mutation UpdateUser(
    $id: ID!
    $email: String
    $name: String
    $supplierId: ID
  ) {
    admin {
      updateUser(id: $id, email: $email, name: $name, supplierId: $supplierId) {
        id
        email
        name
        role
        supplierId
      }
    }
  }
`;

const DELETE_USER = gql`
  mutation DeleteUser($id: ID!) {
    admin {
      deleteUser(id: $id)
    }
  }
`;

// Deprecated in UI: retained in schema but not used here
// const GENERATE_ACCESS_LINK = gql`
//   mutation GenerateAccessLink($userId: ID!, $clientId: ID!) {
//     admin {
//       generateAccessLink(userId: $userId, clientId: $clientId) {
//         url
//         expiresAt
//       }
//     }
//   }
// `;

const GENERATE_MAGIC_LINK = gql`
  mutation GenerateMagicLink($userId: ID!) {
    admin {
      generateMagicLink(userId: $userId) {
        url
        expiresAt
      }
    }
  }
`;

const SEND_MAGIC_LINK = gql`
  mutation SendMagicLink($userId: ID!) {
    admin {
      sendMagicLink(userId: $userId) {
        messageId
        expiresAt
        success
        magicLinkUrl
      }
    }
  }
`;

const ADMIN_USER_LOGIN_HISTORY = gql`
  query AdminUserLoginHistory($userId: ID!, $limit: Int) {
    admin {
      userLoginHistory(userId: $userId, limit: $limit) {
        user {
          id
          name
          email
          role
        }
        entries {
          id
          timestamp
          eventType
          success
          ipAddress
          userAgent
          failureReason
          generatedBy
          tokenExpiry
          messageId
        }
      }
    }
  }
`;

const UserListContainer = styled.div`
  margin-top: 20px;
`;

const FormOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const FormContainer = styled.div`
  background-color: white;
  padding: 24px;
  border-radius: 4px;
  width: 400px;
  max-width: 90%;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const FormTitle = styled.h2`
  margin-top: 0;
  font-size: 20px;
  margin-bottom: 16px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const FormField = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;

  &:focus {
    border-color: #40a9ff;
    outline: none;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;

  &:focus {
    border-color: #40a9ff;
    outline: none;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  gap: 12px;
`;

const ErrorMessage = styled.div`
  color: #ff4d4f;
  margin-top: 10px;
  font-size: 14px;
`;

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const AddButtonContainer = styled.div`
  margin-bottom: 16px;
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 20px;
  color: #666;
  font-style: italic;
`;

const AccessLinkWrapper = styled.div`
  margin-top: 15px;
  padding: 10px;
  background-color: #f9f9f9;
  border-radius: 4px;
  position: relative;
`;

const AccessLink = styled.input`
  width: 100%;
  padding: 8px;
  padding-right: 60px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;
  background-color: #fff;
  cursor: text;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CopyButton = styled.button`
  position: absolute;
  right: 15px;
  top: 15px;
  padding: 4px 8px;
  font-size: 12px;
  z-index: 1;
  border-radius: 4px;
  cursor: pointer;
  background-color: #1890ff;
  color: white;
  border: 1px solid #1890ff;
  transition: all 0.2s;

  &:hover {
    background-color: #40a9ff;
    border-color: #40a9ff;
  }
`;

export type UserListRefType = {
  refetchUsers: () => void;
};

interface Supplier {
  id: string;
  name: string;
  subfolder: string;
  clientId: string;
  client?: {
    clientId: string;
    client_name: string;
  };
}

interface Client {
  clientId: string;
  client_name: string;
  folder: string;
  upload_complete: boolean;
}

interface User {
  id: string;
  email: string | null;
  name: string;
  role: string;
  clientId: string | null;
  client: {
    clientId: string;
    client_name: string;
    folder: string;
  } | null;
  supplierId: string | null;
  supplier: {
    id: string;
    name: string;
    subfolder: string;
  } | null;
  createdAt: string;
  updatedAt: string;
}

interface UserFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    name: string;
    email?: string;
    role: string;
    clientId?: string;
    supplierId?: string;
    supplierName?: string;
    supplierSubfolder?: string;
  }) => void;
  initialData?: {
    id: string;
    email?: string;
    name: string;
    role: string;
  };
  title: string;
  submitLabel: string;
}

const UserForm: React.FC<UserFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  title,
  submitLabel,
}) => {
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    name: initialData?.name || "",
    email: initialData?.email || "",
    role: initialData?.role || "CUSTOMER",
    clientId: "",
    supplierId: "",
    supplierName: "",
    supplierSubfolder: "",
  });
  const [error, setError] = useState("");
  const { data: clientsData } = useQuery(ADMIN_CLIENTS);
  const { data: suppliersData } = useQuery(ADMIN_SUPPLIERS);
  const clients = clientsData?.admin?.clients || [];
  const allSuppliers = suppliersData?.admin?.suppliers || [];
  const [showClientSelect, setShowClientSelect] = useState(false);
  const [showSupplierSelect, setShowSupplierSelect] = useState(false);
  const [filteredSuppliers, setFilteredSuppliers] = useState<Supplier[]>([]);

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || "",
        email: initialData.email || "",
        role: initialData.role,
        clientId: "",
        supplierId: "",
        supplierName: "",
        supplierSubfolder: "",
      });
    } else {
      setFormData({
        name: "",
        email: "",
        role: "CUSTOMER",
        clientId: clients.length > 0 ? clients[0].clientId : "",
        supplierId: "",
        supplierName: "",
        supplierSubfolder: "",
      });
    }
  }, [initialData, isOpen, clients]);

  // Autofocus name when opening create form
  useEffect(() => {
    if (isOpen && !initialData && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isOpen, initialData]);

  useEffect(() => {
    // Show client select for non-admin roles
    setShowClientSelect(formData.role !== "ADMIN" && !initialData);

    // Show supplier select for supplier role
    setShowSupplierSelect(formData.role === "SUPPLIER" && !initialData);
  }, [formData.role, initialData]);

  // Filter suppliers when clientId changes
  useEffect(() => {
    if (formData.clientId && allSuppliers.length > 0) {
      const suppliers = allSuppliers.filter(
        (supplier: Supplier) => supplier.clientId === formData.clientId,
      );
      setFilteredSuppliers(suppliers);

      // Reset supplierId if the selected client has no suppliers or if the current supplierId doesn't match any of the filtered suppliers
      if (suppliers.length > 0) {
        const supplierExists = suppliers.some(
          (s: Supplier) => s.id === formData.supplierId,
        );
        if (!supplierExists) {
          setFormData((prev) => ({
            ...prev,
            supplierId: suppliers[0].id,
            supplierName: suppliers[0].name,
            supplierSubfolder: suppliers[0].subfolder,
          }));
        }
      } else {
        setFormData((prev) => ({
          ...prev,
          supplierId: "",
          supplierName: "",
          supplierSubfolder: "",
        }));
      }
    } else {
      setFilteredSuppliers([]);
    }
  }, [formData.clientId, allSuppliers]);

  if (!isOpen) return null;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    if (name === "supplierId" && value) {
      // When supplier is selected, update supplier name and subfolder
      const selectedSupplier = filteredSuppliers.find((s) => s.id === value);
      if (selectedSupplier) {
        setFormData({
          ...formData,
          supplierId: value,
          supplierName: selectedSupplier.name,
          supplierSubfolder: selectedSupplier.subfolder,
        });
        return;
      }
    }

    setFormData({
      ...formData,
      [name]: value,
    });

    // Update dependent fields
    if (name === "role") {
      setShowClientSelect(value !== "ADMIN");
      setShowSupplierSelect(value === "SUPPLIER");
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Validate form
    if (!formData.name.trim()) {
      setError("Name is required");
      return;
    }

    // Require email and role on create
    if (!initialData && !formData.email.trim()) {
      setError("Email is required");
      return;
    }
    if (!initialData && !formData.role) {
      setError("Role is required");
      return;
    }

    // Additional validation for non-admin roles
    if (!initialData && formData.role !== "ADMIN" && !formData.clientId) {
      setError("Client selection is required for this role");
      return;
    }

    // Additional validation for supplier role
    if (!initialData && formData.role === "SUPPLIER") {
      if (!formData.supplierId) {
        setError("Supplier selection is required");
        return;
      }
    }

    // Confirmation dialog for ADMIN role creation
    if (!initialData && formData.role === "ADMIN") {
      const confirmed = window.confirm(
        "You are about to create an Admin user with full system access. " +
          "This user will be able to manage all users, files, clients, suppliers, and settings. " +
          "\n\nAre you sure you want to proceed?",
      );
      if (!confirmed) {
        return;
      }
    }

    onSubmit(formData);
  };

  return (
    <FormOverlay>
      <FormContainer>
        <FormTitle>{title}</FormTitle>
        <Form onSubmit={handleSubmit}>
          <FormField>
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              name="name"
              ref={nameInputRef}
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter user name"
              required
            />
          </FormField>

          <FormField>
            <Label htmlFor="email">{`Email${!initialData ? " *" : ""}`}</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={"Enter user email"}
              required={!initialData}
            />
          </FormField>

          <FormField>
            <Label htmlFor="role">{`Role${!initialData ? " *" : ""}`}</Label>
            <Select
              id="role"
              name="role"
              value={formData.role}
              onChange={handleChange}
              disabled={!!initialData} // Disable for existing users
              required={!initialData}
            >
              <option value="ADMIN">Admin</option>
              <option value="CUSTOMER">Customer</option>
              <option value="SUPPLIER">Supplier</option>
              <option value="PORTWAY_EMPLOYEE">Portway Employee</option>
            </Select>
          </FormField>

          {showClientSelect && (
            <FormField>
              <Label htmlFor="clientId">Client *</Label>
              <Select
                id="clientId"
                name="clientId"
                value={formData.clientId}
                onChange={handleChange}
                required
              >
                <option value="">Select a client</option>
                {clients.map((client: Client) => (
                  <option key={client.clientId} value={client.clientId}>
                    {client.client_name}
                  </option>
                ))}
              </Select>
            </FormField>
          )}

          {showSupplierSelect && (
            <FormField>
              <Label htmlFor="supplierId">Supplier *</Label>
              {filteredSuppliers.length > 0 ? (
                <Select
                  id="supplierId"
                  name="supplierId"
                  value={formData.supplierId}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select a supplier</option>
                  {filteredSuppliers.map((supplier) => (
                    <option key={supplier.id} value={supplier.id}>
                      {supplier.name} ({supplier.subfolder})
                    </option>
                  ))}
                </Select>
              ) : (
                <ErrorMessage style={{ marginTop: 0 }}>
                  No suppliers found for the selected client. Please add a
                  supplier first.
                </ErrorMessage>
              )}
            </FormField>
          )}

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <ButtonGroup>
            <LatitudeButton
              label="Cancel"
              intent="basic"
              kind="hollow"
              size="m"
              isLoading={false}
              width="responsive"
              type="button"
              disabled={false}
              onClick={onClose}
            >
              Cancel
            </LatitudeButton>
            <LatitudeButton
              label={submitLabel}
              intent="basic"
              kind="solid"
              size="m"
              isLoading={false}
              width="responsive"
              type="submit"
              disabled={false}
            >
              {submitLabel}
            </LatitudeButton>
          </ButtonGroup>
        </Form>
      </FormContainer>
    </FormOverlay>
  );
};

interface AccessLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  url: string;
}

const AccessLinkModal: React.FC<AccessLinkModalProps> = ({
  isOpen,
  onClose,
  url,
}) => {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const handleCopy = () => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <FormOverlay>
      <FormContainer>
        <FormTitle>Access Link Generated</FormTitle>
        <p>Share this link with the user to provide access:</p>

        <AccessLinkWrapper>
          <AccessLink
            type="text"
            value={url}
            readOnly
            onClick={(e) => e.currentTarget.select()}
          />
          <CopyButton onClick={handleCopy}>
            {copied ? "Copied!" : "Copy"}
          </CopyButton>
        </AccessLinkWrapper>

        <ButtonGroup>
          <LatitudeButton
            label="Close"
            intent="basic"
            kind="solid"
            size="m"
            isLoading={false}
            width="responsive"
            type="button"
            disabled={false}
            onClick={onClose}
          >
            Close
          </LatitudeButton>
        </ButtonGroup>
      </FormContainer>
    </FormOverlay>
  );
};

/**
 * EmailSentModal - Modal to show confirmation after email is sent
 */
interface EmailSentModalProps {
  isOpen: boolean;
  onClose: () => void;
  success: boolean;
  error?: string;
}

const EmailSentModal: React.FC<EmailSentModalProps> = ({
  isOpen,
  onClose,
  success,
  error,
}) => {
  if (!isOpen) return null;

  return (
    <FormOverlay>
      <FormContainer>
        <FormTitle>
          {success ? "Email Sent Successfully" : "Email Failed"}
        </FormTitle>
        {success ? (
          <p>The magic link has been successfully sent via email.</p>
        ) : (
          <ErrorMessage>
            {error || "Failed to send magic link. Please try again."}
          </ErrorMessage>
        )}

        <ButtonGroup>
          <LatitudeButton
            label="OK"
            intent="basic"
            kind="solid"
            size="m"
            isLoading={false}
            width="responsive"
            type="button"
            disabled={false}
            onClick={onClose}
          >
            OK
          </LatitudeButton>
        </ButtonGroup>
      </FormContainer>
    </FormOverlay>
  );
};

const AdminUserList = forwardRef<UserListRefType>((_, ref) => {
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [isAccessLinkModalOpen, setAccessLinkModalOpen] = useState(false);
  const [isEmailSentModalOpen, setEmailSentModalOpen] = useState(false);
  const [isLoginHistoryModalOpen, setLoginHistoryModalOpen] = useState(false);
  const [emailSentInfo, setEmailSentInfo] = useState({
    success: false,
    expiresAt: "",
    error: "",
  });
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedUserForHistory, setSelectedUserForHistory] = useState<{
    id: string;
    name: string | null;
    email: string;
    role: string;
  } | null>(null);
  const [accessLink, setAccessLink] = useState({ url: "", expiresAt: "" });

  // State for custom delete confirmation dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState<{
    id: string;
    name: string;
    role: string;
  } | null>(null);

  const { loading, error, data, refetch } = useQuery(ADMIN_USERS);

  // Lazy query for login history (fetches on demand)
  const [
    fetchLoginHistory,
    {
      data: loginHistoryData,
      loading: loginHistoryLoading,
      error: loginHistoryError,
    },
  ] = useLazyQuery(ADMIN_USER_LOGIN_HISTORY);

  const [createUser] = useMutation(CREATE_USER, {
    onCompleted: () => {
      setCreateModalOpen(false);
      refetch();
    },
  });

  const [updateUser] = useMutation(UPDATE_USER, {
    onCompleted: () => {
      setEditModalOpen(false);
      refetch();
    },
  });

  const [deleteUser] = useMutation(DELETE_USER, {
    onCompleted: () => {
      refetch();
    },
  });

  // Deprecated in UI: retained for backward compatibility of GraphQL schema
  // const [generateAccessLink] = useMutation(GENERATE_ACCESS_LINK, {
  //   onCompleted: (data) => {
  //     const linkData = data?.admin?.generateAccessLink;
  //     if (linkData) {
  //       setAccessLink({
  //         url: linkData.url,
  //         expiresAt: linkData.expiresAt,
  //       });
  //       setAccessLinkModalOpen(true);
  //     }
  //   },
  // });

  const [generateMagicLink] = useMutation(GENERATE_MAGIC_LINK, {
    onCompleted: (data) => {
      const linkData = data?.admin?.generateMagicLink;
      if (linkData) {
        setAccessLink({ url: linkData.url, expiresAt: linkData.expiresAt });
        setAccessLinkModalOpen(true);
      }
    },
  });

  const [sendMagicLink] = useMutation(SEND_MAGIC_LINK, {
    onCompleted: (data) => {
      const res = data?.admin?.sendMagicLink;
      if (res?.success) {
        setEmailSentInfo({
          success: true,
          expiresAt: res.expiresAt || "",
          error: "",
        });
        setEmailSentModalOpen(true);
      } else {
        setEmailSentInfo({
          success: false,
          expiresAt: "",
          error: "Failed to send magic link. Please try again.",
        });
        setEmailSentModalOpen(true);
      }
    },
    onError: (error) => {
      console.error("Error sending magic link:", error);
      setEmailSentInfo({
        success: false,
        expiresAt: "",
        error: error.message,
      });
      setEmailSentModalOpen(true);
    },
  });

  // Handle send magic link via email
  // Note: ADMIN users authenticate via Okta/CloudFlare and cannot use magic links
  const handleSendMagicLink = (userId: string) => {
    // Defensive check: ensure we're not trying to send magic link to ADMIN user
    const targetUser = users.find((u: any) => u.id === userId);
    if (targetUser?.role === "ADMIN") {
      alert(
        "Cannot send magic link to ADMIN users.\n\nADMIN users authenticate via Okta/CloudFlare, not magic links.",
      );
      return;
    }

    sendMagicLink({ variables: { userId } });
  };

  // Handle view login history button click
  const handleViewLoginHistory = useCallback(
    (user: any) => {
      console.log("[AdminUserList] Opening login history for user:", user.id);
      setSelectedUserForHistory({
        id: user.id.toString(),
        name: user.name,
        email: user.email,
        role: user.role,
      });
      setLoginHistoryModalOpen(true);

      // Fetch login history data
      fetchLoginHistory({
        variables: {
          userId: user.id.toString(),
          limit: 100,
        },
      });
    },
    [fetchLoginHistory],
  );

  // Expose refetch function to parent
  useImperativeHandle(ref, () => ({
    refetchUsers: refetch,
  }));

  const users = data?.admin?.users || [];

  const handleCreateUser = (data: {
    name: string;
    email?: string;
    role: string;
    clientId?: string;
    supplierId?: string;
    supplierName?: string;
    supplierSubfolder?: string;
  }) => {
    createUser({
      variables: {
        name: data.name,
        email: data.email || null,
        role: data.role,
        // Admin users should never be associated with a client
        clientId: data.role === "ADMIN" ? null : data.clientId || null,
        supplierId: data.supplierId || null,
        supplierName: data.supplierName || null,
        supplierSubfolder: data.supplierSubfolder || null,
      },
    });
  };

  const handleUpdateUser = (data: { name: string; email?: string }) => {
    updateUser({
      variables: {
        id: selectedUser?.id,
        email: data.email || null,
        name: data.name,
        supplierId: selectedUser?.supplierId,
      },
    });
  };

  // Handle edit button click
  const handleEdit = (user: User | any) => {
    setSelectedUser(user as User);
    setEditModalOpen(true);
  };

  // Handle generate link button click
  // Note: ADMIN users authenticate via Okta/CloudFlare and cannot use magic links
  const handleGenerateLink = (userId: string) => {
    // Defensive check: ensure we're not trying to generate magic link for ADMIN user
    const targetUser = users.find((u: any) => u.id === userId);
    if (targetUser?.role === "ADMIN") {
      alert(
        "Cannot generate magic link for ADMIN users.\n\nADMIN users authenticate via Okta/CloudFlare, not magic links.",
      );
      return;
    }

    generateMagicLink({
      variables: { userId },
      onError: (error) => {
        console.error("Error generating magic link:", error);
        // Skip alert when session-expired modal will handle the error
        if (!isSessionExpiredError()) {
          alert("Error generating magic link: " + error.message);
        }
      },
    });
  };

  // Handle delete button click - shows confirmation dialog
  const handleDelete = (id: string) => {
    const user = users.find((u: any) => u.id === id);
    if (!user) return;

    setUserToDelete({
      id,
      name: user.name,
      role: user.role,
    });
    setShowDeleteDialog(true);
  };

  // Confirm delete after dialog confirmation
  const confirmDelete = async () => {
    if (!userToDelete) return;

    try {
      await deleteUser({
        variables: { id: userToDelete.id },
      });
      setShowDeleteDialog(false);
      setUserToDelete(null);
    } catch (error) {
      console.error("Error deleting user:", error);
      // Skip alert when session-expired modal will handle the error
      if (!isSessionExpiredError()) {
        alert("Failed to delete user. Please try again.");
      }
      setShowDeleteDialog(false);
      setUserToDelete(null);
    }
  };

  if (loading) {
    return <LoaderContainer>Loading users...</LoaderContainer>;
  }

  if (error) {
    return <div>Error loading users: {error.message}</div>;
  }

  return (
    <UserListContainer>
      <AddButtonContainer>
        <LatitudeButton
          label="Add New User"
          intent="basic"
          kind="hollow"
          size="m"
          isLoading={false}
          width="responsive"
          type="button"
          disabled={false}
          onClick={() => setCreateModalOpen(true)}
        >
          Add New User
        </LatitudeButton>
      </AddButtonContainer>

      {users.length === 0 ? (
        <NoDataMessage>No users found. Add your first user.</NoDataMessage>
      ) : (
        <AdminUserGrid
          rows={users}
          onEdit={handleEdit}
          onGenerateLink={handleGenerateLink}
          onSendEmail={handleSendMagicLink}
          onDelete={handleDelete}
          onViewLoginHistory={handleViewLoginHistory}
        />
      )}

      {/* Custom delete confirmation dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        title="File Uploader"
        message={
          userToDelete?.role === "ADMIN"
            ? `⚠️ WARNING: You are about to delete an Admin user!\n\nUser: "${userToDelete?.name}"\nRole: ADMIN\n\nAdmin users have full system access including:\n• Managing all users, files, clients, and suppliers\n• Viewing and modifying all system data\n• Generating access links and magic links\n\nDeleting this admin will permanently remove their access and cannot be undone.\n\nAre you absolutely sure you want to proceed?`
            : `Are you sure you want to permanently delete user "${userToDelete?.name}"?\n\nThis action cannot be undone.`
        }
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowDeleteDialog(false);
          setUserToDelete(null);
        }}
      />

      <UserForm
        isOpen={isCreateModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSubmit={handleCreateUser}
        title="Add New User"
        submitLabel="Create"
      />

      <UserForm
        isOpen={isEditModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSubmit={handleUpdateUser}
        initialData={
          selectedUser
            ? {
                id: selectedUser.id,
                email: selectedUser.email || undefined,
                name: selectedUser.name,
                role: selectedUser.role,
              }
            : undefined
        }
        title="Edit User"
        submitLabel="Update"
      />

      <AccessLinkModal
        isOpen={isAccessLinkModalOpen}
        onClose={() => setAccessLinkModalOpen(false)}
        url={accessLink.url}
      />

      <EmailSentModal
        isOpen={isEmailSentModalOpen}
        onClose={() => setEmailSentModalOpen(false)}
        success={emailSentInfo.success}
        error={emailSentInfo.error}
      />

      {/* Login History Modal */}
      {isLoginHistoryModalOpen && (
        <UserLoginHistoryModal
          isOpen={isLoginHistoryModalOpen}
          onClose={() => {
            setLoginHistoryModalOpen(false);
            setSelectedUserForHistory(null);
          }}
          user={selectedUserForHistory}
          entries={loginHistoryData?.admin?.userLoginHistory?.entries || []}
          loading={loginHistoryLoading}
          error={loginHistoryError?.message || null}
        />
      )}
    </UserListContainer>
  );
});

export default AdminUserList;
